// @ts-strict-ignore
import Link from 'next/link'
import React, { memo, ReactNode, useCallback, useMemo } from 'react'

import { formatPrice } from '@/shared/lib/utils/price/format-price'
import { _TProduct, TPrice, TProductCode } from '@/shared/types/deprecated-types'
import { OrderType } from '@/shared/types/product-types'
import { ProductPrice } from '@/shared/ui/product/ProductPrice'

import { IMAGE_SIZE_DESKTOP, IMAGE_SIZE_MOBILE } from './constants'
import { ProductImage } from './product-image'
import { QuantityControl } from './quantity-control'
import { RemoveButton } from './remove-button'

import styles from './basket-item-table.module.scss'

export type BasketItemTableProps = {
	ariaLabel: string
	link: string
	imageSrc: string
	title: string
	price: TPrice
	bonus?: number
	idBasket?: number
	onDeleteButtonClick: (idBasket: number, relatedId: number) => void
	onQuantityChange: (idBasket: number, quantity: number, relatedId?: number) => void
	initialCount: number
	productCode: TProductCode
	orderType: _TProduct[`ORDER_TYPE`]
	relatedId?: number
	productId: number
	footer?: ReactNode
}

export const BasketItemTable = memo(
	({
		ariaLabel,
		title,
		link,
		imageSrc,
		bonus,
		price,
		initialCount,
		idBasket,
		onQuantityChange,
		onDeleteButtonClick,
		relatedId,
		orderType,
		footer = null
	}: BasketItemTableProps): JSX.Element => {
		const totalPrice: TPrice = useMemo(
			() => ({
				...price,
				VALUE: price.VALUE * initialCount
			}),
			[price, initialCount]
		)

		const handleQuantityChange = useCallback(
			(quantity: number) => {
				onQuantityChange(idBasket, quantity, relatedId)
			},
			[idBasket, relatedId, onQuantityChange]
		)

		const handleRemove = useCallback(() => {
			onDeleteButtonClick(idBasket, relatedId)
		}, [idBasket, relatedId, onDeleteButtonClick])

		const isHiddenBuy = orderType === OrderType.HideBuy
		const rootClassName = [styles.basketItemTable]
		if (isHiddenBuy) rootClassName.push(styles.basketItemTable_hiddenBuy)

		return (
			<div aria-label={ariaLabel} className={rootClassName.join(' ')}>
				<ProductImage
					link={link}
					title={title}
					imageSrc={imageSrc}
					width={IMAGE_SIZE_DESKTOP.width}
					height={IMAGE_SIZE_DESKTOP.height}
				/>
				<div className={styles.content}>
					<ProductImage
						link={link}
						title={title}
						imageSrc={imageSrc}
						width={IMAGE_SIZE_MOBILE.width}
						height={IMAGE_SIZE_MOBILE.height}
						isMobile
					/>

					<div className={styles.titleCell}>
						<Link prefetch={false} href={link} className={styles.titleLink}>
							{title}
						</Link>
					</div>

					<QuantityControl orderType={orderType} initialCount={initialCount} onCountChange={handleQuantityChange} />

					<div className={styles.totalCell}>
						<ProductPrice price={totalPrice} />
						{!!bonus && <small className={styles.bonuses}>+{formatPrice(bonus, `decimal`)} баллов</small>}
					</div>

					<RemoveButton onRemove={handleRemove} />

					{footer && <div className={styles.footer}>{footer}</div>}
				</div>
				<RemoveButton onRemove={handleRemove} isMobile />
			</div>
		)
	}
)
